import { useCallback } from "react";
import type { Action } from "@/react/components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import { useAiWorkflowActivationModal } from "./useAiWorkflowActivationModal";

interface UseWorkflowToggleWithAiModalOptions {
  actions?: Action[];
  isActive: boolean;
  onToggle: () => void;
  steps?: Action["action_type"][];
  workflowId?: string;
}

/**
 * Hook that encapsulates the common pattern of workflow toggles with AI modal integration.
 * Handles the logic for showing AI modal when activating workflows with AI actions.
 */
export const useWorkflowToggleWithAiModal = ({
  isActive,
  onToggle,
  actions,
  steps,
  workflowId,
}: UseWorkflowToggleWithAiModalOptions) => {
  const { handleActivationAttempt, isModalVisible } =
    useAiWorkflowActivationModal({
      actions,
      steps,
      workflowId,
    });

  // If the modal is visible, don't show the toggle as active
  const isEffectivelyActive = isActive && !isModalVisible;

  const handleToggleChange = useCallback(
    (newStatus: boolean) => {
      // Check if we're trying to activate a non-active workflow
      // eslint-disable-next-line sonarjs/no-selector-parameter -- Disabled for plugin version update. Fix when possible.
      if (!isActive && newStatus) {
        handleActivationAttempt(onToggle);
      } else {
        onToggle();
      }
    },
    [isActive, handleActivationAttempt, onToggle],
  );

  return {
    isEffectivelyActive,
    handleToggleChange,
    isModalVisible,
    handleActivationAttempt,
  };
};
